import React, { useCallback, useEffect, useRef, useState } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import {
  billingError, billingErrorBody, buildClaim, getClaim, saveClaim, setClaimStatus, validateClaim,
} from "./billingApi";
import { Button, Card, DummyPill, Field, Messages, Pill, StatusPill, TextInput, inputCls, money, setIn } from "./ui";

/*
 * One visit's claim (Billing > Claims > a visit). The server builds a draft from our data (signed note
 * diagnoses in note order, E/M from the visit level, orders via lcpt_master, fee schedule charges) or,
 * on "Prefill from Athena bill", from the claim Athena billed for the visit. Nothing is stored until
 * "Save draft"; "Mark ready" needs a saved claim without errors. Edits re-check the claim on the server
 * (debounced) so problems show inline.
 */

const MAX_DX = 12;
const FILING = ["CI", "BL", "MB", "MC", "HM", "16", "12", "CH", "WC", "ZZ"];

const csv = (arr) => (arr || []).join(", ");
const parseList = (s) => String(s || "").split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
const parsePointers = (s) => parseList(s).map((x) => Number(x)).filter((n) => Number.isInteger(n) && n > 0);

function editable(claim) {
  // The fields the server accepts on save (store.EDITABLE_FIELDS) + source.
  const keys = ["subscriber", "patient", "payer", "pos", "frequency", "onset_date", "prior_auth", "referral_number",
                "diagnoses", "lines", "rendering_providerid", "departmentid", "telehealth", "source"];
  return Object.fromEntries(keys.filter((k) => k in (claim || {})).map((k) => [k, claim[k]]));
}

const ClaimEditor = ({ appointmentid, config, onClose, onChanged, toast }) => {
  const [claim, setClaim] = useState(null);
  const [saved, setSaved] = useState(null);         // {id, status, version, ...} or null
  const [validation, setValidation] = useState({ errors: [], warnings: [] });
  const [athenaAvailable, setAthenaAvailable] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newDx, setNewDx] = useState("");
  const timer = useRef(null);

  const apply = (body) => {
    setClaim(body.claim);
    setSaved(body.saved || null);
    setValidation(body.validation || { errors: [], warnings: [] });
    setAthenaAvailable(Boolean(body.athena_available));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await getClaim(appointmentid));
      setDirty(false);
    } catch (e) {
      toast(billingError(e, "Could not load the claim"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const recheck = (next) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await validateClaim(appointmentid, editable(next));
        setValidation(r.validation || { errors: [], warnings: [] });
      } catch (e) { /* the next save shows it */ }
    }, 600);
  };

  const update = (path, value) => {
    setClaim((c) => {
      const next = setIn(c, path, value);
      recheck(next);
      return next;
    });
    setDirty(true);
  };

  const rebuild = async (mode) => {
    setBusy(true);
    try {
      const body = await buildClaim(appointmentid, mode);
      setClaim(body.claim);
      setValidation(body.validation || { errors: [], warnings: [] });
      setDirty(true);
      toast(mode === "athena" ? "Prefilled from the Athena bill. Review it, then Save draft."
                              : "Rebuilt from our data. Review it, then Save draft.", "info");
    } catch (e) {
      toast(billingError(e, "Could not build the claim"), "error");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = await saveClaim(appointmentid, editable(claim), saved?.version);
      apply(body);
      setDirty(false);
      onChanged && onChanged();
      toast("Draft saved", "success");
    } catch (e) {
      toast(billingError(e, "Could not save"), "error");
    } finally {
      setBusy(false);
    }
  };

  const status = async (to) => {
    setBusy(true);
    try {
      await setClaimStatus(appointmentid, to, saved?.version);
      await load();
      onChanged && onChanged();
      toast(to === "ready" ? "Marked ready for a file" : "Moved back to draft", "success");
    } catch (e) {
      const body = billingErrorBody(e);
      if (body?.detail?.errors) setValidation(body.detail);
      toast(billingError(e, "Could not change the status"), "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !claim) {
    return <div className="flex justify-center py-16"><CircularProgress size={28} /></div>;
  }

  const locked = saved && ["submitted_athena", "submitted_clearinghouse"].includes(saved.status);
  const sub = claim.subscriber || {};
  const payer = claim.payer || {};
  const dx = claim.diagnoses || [];
  const lines = claim.lines || [];
  const providers = Object.entries(config?.providers || {});
  const facilities = Object.entries(config?.service_facilities || {});
  const total = lines.reduce((s, l) => s + (Number(l.charge) || 0), 0);
  const notes = (claim.notes || []).filter((n) => n.kind !== "error").map((n) => ({ ...n, field: "" }));
  const canReady = saved && saved.status === "draft" && !dirty && !validation.errors.length;
  const payerDummy = /DUMMY/i.test(payer.payer_id || "");

  const moveDx = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= dx.length) return;
    const next = [...dx];
    [next[i], next[j]] = [next[j], next[i]];
    // keep each line pointing at the same codes
    const map = (p) => (p === i + 1 ? j + 1 : p === j + 1 ? i + 1 : p);
    setClaim((c) => {
      const n = { ...c, diagnoses: next, lines: (c.lines || []).map((l) => ({ ...l, pointers: (l.pointers || []).map(map) })) };
      recheck(n);
      return n;
    });
    setDirty(true);
  };
  const removeDx = (i) => {
    setClaim((c) => {
      const n = {
        ...c,
        diagnoses: dx.filter((_, k) => k !== i),
        lines: (c.lines || []).map((l) => ({
          ...l, pointers: (l.pointers || []).filter((p) => p !== i + 1).map((p) => (p > i + 1 ? p - 1 : p)),
        })),
      };
      recheck(n);
      return n;
    });
    setDirty(true);
  };
  const addDx = () => {
    const code = newDx.trim().toUpperCase();
    if (!code) return;
    update("diagnoses", [...dx, code]);
    setNewDx("");
  };
  const setLine = (i, key, value) => update(`lines.${i}.${key}`, value);
  const addLine = () => update("lines", [...lines, { cpt: "", modifiers: [], units: 1, charge: "", pointers: [1],
                                                       dos: claim.dos, description: "", source: "manual" }]);
  const removeLine = (i) => update("lines", lines.filter((_, k) => k !== i));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-[#18181B]">
              {[sub.last, sub.first].filter(Boolean).join(", ") || "Patient"}
            </h2>
            <StatusPill status={saved?.status} />
            <Pill className="bg-[#F3F4F6] text-[#374151]">{claim.source === "athena_prefill" ? "Athena prefill" : "Our coding"}</Pill>
            {dirty && <Pill className="bg-amber-50 text-amber-700">Unsaved changes</Pill>}
          </div>
          <div className="text-[12px] text-[#6B7280] mt-0.5">
            Visit {appointmentid} · DOS {claim.dos} · Patient id {claim.patientid} · Total {money(total)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {athenaAvailable && !locked && (
            <Button kind="outline" disabled={busy} onClick={() => rebuild("athena")}>Prefill from Athena bill</Button>
          )}
          {!locked && <Button kind="ghost" disabled={busy} onClick={() => rebuild("ours")}>Rebuild from our data</Button>}
          {!locked && <Button kind="outline" disabled={busy || (!dirty && saved)} onClick={save}>Save draft</Button>}
          {saved?.status === "ready" && <Button kind="ghost" disabled={busy} onClick={() => status("draft")}>Back to draft</Button>}
          {!locked && (
            <Button disabled={busy || !canReady} onClick={() => status("ready")}
              title={!saved ? "Save the draft first" : dirty ? "Save your changes first" : validation.errors.length ? "Fix the errors first" : ""}>
              Mark ready
            </Button>
          )}
          <button type="button" aria-label="Close claim" onClick={onClose} className="p-1 rounded hover:bg-[#F3F4F6]">
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      <Card title="Checks">
        <Messages errors={validation.errors || []} warnings={validation.warnings || []} notes={notes} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Patient / subscriber">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Last name"><TextInput value={sub.last} disabled={locked} onChange={(v) => update("subscriber.last", v)} /></Field>
            <Field label="First name"><TextInput value={sub.first} disabled={locked} onChange={(v) => update("subscriber.first", v)} /></Field>
            <Field label="Date of birth"><TextInput type="date" value={sub.dob || ""} disabled={locked} onChange={(v) => update("subscriber.dob", v)} /></Field>
            <Field label="Gender">
              <select className={inputCls()} value={sub.gender || "U"} disabled={locked} onChange={(e) => update("subscriber.gender", e.target.value)}>
                <option value="F">F</option><option value="M">M</option><option value="U">U</option>
              </select>
            </Field>
            <Field label="Member id"><TextInput value={sub.member_id} disabled={locked} onChange={(v) => update("subscriber.member_id", v)} /></Field>
            <Field label="Group number"><TextInput value={sub.group_number} disabled={locked} onChange={(v) => update("subscriber.group_number", v)} /></Field>
            <Field label="Street" className="col-span-2"><TextInput value={sub.address?.line1} disabled={locked} onChange={(v) => update("subscriber.address.line1", v)} /></Field>
            <Field label="City"><TextInput value={sub.address?.city} disabled={locked} onChange={(v) => update("subscriber.address.city", v)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="State"><TextInput value={sub.address?.state} disabled={locked} maxLength={2} onChange={(v) => update("subscriber.address.state", v.toUpperCase())} /></Field>
              <Field label="ZIP"><TextInput value={sub.address?.zip} disabled={locked} maxLength={9} onChange={(v) => update("subscriber.address.zip", v.replace(/\D/g, ""))} /></Field>
            </div>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-2">Subscriber = patient (relationship 18). Dependents are not supported yet.</p>
        </Card>

        <Card title="Payer" right={payerDummy ? <DummyPill /> : null}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Payer name" className="col-span-2"><TextInput value={payer.name} disabled={locked} onChange={(v) => update("payer.name", v)} /></Field>
            <Field label="Payer id" hint={payerDummy ? "Map it in Config > Payers" : ""}>
              <TextInput value={payer.payer_id} disabled={locked} bad={payerDummy} onChange={(v) => update("payer.payer_id", v)} />
            </Field>
            <Field label="Filing indicator">
              <select className={inputCls()} value={sub.filing_indicator || "CI"} disabled={locked} onChange={(e) => update("subscriber.filing_indicator", e.target.value)}>
                {FILING.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <div className="col-span-2 text-[11px] text-[#9CA3AF]">
              Plan on the visit: {payer.plan_name || "—"}{payer.package_id ? ` (Athena package ${payer.package_id})` : ""}
            </div>
          </div>
        </Card>

        <Card title="Provider and place">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rendering provider" className="col-span-2">
              <select className={inputCls()} value={claim.rendering_providerid ?? ""} disabled={locked}
                onChange={(e) => update("rendering_providerid", e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {providers.map(([id, p]) => <option key={id} value={id}>{p.name || `${p.first} ${p.last}`} · NPI {p.npi}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select className={inputCls()} value={claim.departmentid ?? ""} disabled={locked}
                onChange={(e) => update("departmentid", e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {facilities.map(([id, f]) => <option key={id} value={id}>{f.name || `Department ${id}`}</option>)}
              </select>
            </Field>
            <Field label="Place of service" hint={claim.telehealth ? "Telehealth visit" : ""}>
              <TextInput value={claim.pos} disabled={locked} maxLength={2} onChange={(v) => update("pos", v.replace(/\D/g, ""))} />
            </Field>
            <Field label="Onset date"><TextInput type="date" value={claim.onset_date || ""} disabled={locked} onChange={(v) => update("onset_date", v || null)} /></Field>
            <Field label="Prior auth"><TextInput value={claim.prior_auth} disabled={locked} onChange={(v) => update("prior_auth", v || null)} /></Field>
          </div>
        </Card>
      </div>

      <Card title={`Diagnoses (${dx.length}/${MAX_DX})`} right={<span className="text-[11px] text-[#6B7280]">Order = pointer number; first is principal</span>}>
        <ol className="divide-y divide-[#F3F4F6]">
          {dx.map((code, i) => (
            <li key={`${code}-${i}`} className="flex items-center gap-3 py-1.5">
              <span className="w-6 text-right text-[12px] font-semibold text-[#6B7280]">{i + 1}</span>
              <span className="font-mono text-[13px] text-[#18181B] w-24">{code}</span>
              {i === 0 && <Pill className="bg-[#EEF2FB] text-[#1D2951]">Principal</Pill>}
              {!locked && (
                <span className="ml-auto flex items-center gap-1">
                  <button type="button" aria-label="Move up" className="p-0.5 rounded hover:bg-[#F3F4F6]" onClick={() => moveDx(i, -1)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></button>
                  <button type="button" aria-label="Move down" className="p-0.5 rounded hover:bg-[#F3F4F6]" onClick={() => moveDx(i, 1)}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></button>
                  <button type="button" aria-label="Remove" className="p-0.5 rounded hover:bg-red-50 text-red-700" onClick={() => removeDx(i)}><CloseIcon sx={{ fontSize: 16 }} /></button>
                </span>
              )}
            </li>
          ))}
        </ol>
        {!locked && dx.length < MAX_DX && (
          <div className="flex items-center gap-2 mt-2">
            <TextInput value={newDx} onChange={setNewDx} placeholder="Add ICD-10 (e.g. E78.5)" className="max-w-[200px]"
              onKeyDown={(e) => { if (e.key === "Enter") addDx(); }} />
            <Button kind="ghost" onClick={addDx}>Add</Button>
          </div>
        )}
      </Card>

      <Card title="Service lines" right={!locked && <Button kind="ghost" onClick={addLine}>Add line</Button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                <th className="px-2 py-1.5 w-8">#</th><th className="px-2 py-1.5">Code</th><th className="px-2 py-1.5">Modifiers</th>
                <th className="px-2 py-1.5 w-20">Units</th><th className="px-2 py-1.5 w-24">Charge</th>
                <th className="px-2 py-1.5">Dx pointers</th><th className="px-2 py-1.5">Description</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-[#F3F4F6] align-top">
                  <td className="px-2 py-1.5 text-[#6B7280]">{i + 1}</td>
                  <td className="px-2 py-1.5"><TextInput value={l.cpt} disabled={locked} className="font-mono w-24" onChange={(v) => setLine(i, "cpt", v.toUpperCase())} /></td>
                  <td className="px-2 py-1.5"><TextInput value={csv(l.modifiers)} disabled={locked} className="w-28" placeholder="25, 95"
                    onChange={(v) => setLine(i, "modifiers", parseList(v))} /></td>
                  <td className="px-2 py-1.5"><TextInput value={l.units} disabled={locked} onChange={(v) => setLine(i, "units", v)} /></td>
                  <td className="px-2 py-1.5"><TextInput value={l.charge} disabled={locked} onChange={(v) => setLine(i, "charge", v)} /></td>
                  <td className="px-2 py-1.5"><TextInput value={csv(l.pointers)} disabled={locked} className="w-24" placeholder="1, 2"
                    onChange={(v) => setLine(i, "pointers", parsePointers(v))} /></td>
                  <td className="px-2 py-1.5 text-[12px] text-[#374151]">
                    {l.description}
                    {l.source ? <span className="block text-[11px] text-[#9CA3AF]">{l.source === "em" ? "E/M from visit level" : l.source === "order" ? "from an order" : l.source}</span> : null}
                  </td>
                  <td className="px-2 py-1.5">
                    {!locked && <button type="button" aria-label={`Remove line ${i + 1}`} className="p-0.5 rounded hover:bg-red-50 text-red-700" onClick={() => removeLine(i)}><CloseIcon sx={{ fontSize: 16 }} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#E5E7EB]">
                <td colSpan={4} className="px-2 py-1.5 text-right text-[12px] text-[#6B7280]">Total</td>
                <td className="px-2 py-1.5 font-semibold">{money(total)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ClaimEditor;
